import type { ExecutionTraceStep } from "../data/types";

export const TRACE_STEP_PAUSE_MS = [500, 1000] as const;
export const TRACE_SETTLE_MS = 140;
export const TRACE_LINE_GAP_MS = 50;

export type StreamToken = {
  type: "code" | "text";
  value: string;
};

const TOKEN_RE = /\s+|[A-Za-z0-9_./:-]+|[\u3400-\u9FFF]|./g;

export function tokenizeForStream(text: string): StreamToken[] {
  const chunks = text.split(/(`[^`]+`)/g).filter((chunk) => chunk.length > 0);
  const tokens: StreamToken[] = [];

  for (const chunk of chunks) {
    if (chunk.startsWith("`") && chunk.endsWith("`") && chunk.length >= 2) {
      tokens.push({ type: "code", value: chunk.slice(1, -1) });
      continue;
    }

    const parts = chunk.match(TOKEN_RE) ?? [chunk];
    for (const part of parts) {
      tokens.push({ type: "text", value: part });
    }
  }

  return tokens;
}

/** ChatGPT-like pace: CJK/char tokens ~12ms, Latin chunks a bit faster. */
export function streamIntervalMs(token: StreamToken): number {
  if (token.type === "code") return 18;
  if (/\s/.test(token.value)) return 5;
  if (/[\u3400-\u9FFF]/.test(token.value)) return 12;
  return 8;
}

export function estimateStreamMs(text: string, asCode = false): number {
  const tokens = asCode
    ? [{ type: "code" as const, value: text }]
    : tokenizeForStream(text);
  if (tokens.length === 0) return 0;
  return tokens.reduce((sum, token) => sum + streamIntervalMs(token), 0);
}

export function countStepLines(step: ExecutionTraceStep): number {
  return 1 + (step.bullets?.length ?? 0) + (step.note ? 1 : 0);
}

export function estimateStepLineStreamMs(
  step: ExecutionTraceStep,
  lineIndex: number,
): number {
  if (lineIndex <= 0) {
    return Math.max(
      estimateStreamMs(step.title),
      step.code ? estimateStreamMs(step.code, true) : 0,
      step.detail ? estimateStreamMs(step.detail) : 0,
    );
  }

  const bulletIndex = lineIndex - 1;
  const bullets = step.bullets ?? [];
  if (bulletIndex < bullets.length) {
    return estimateStreamMs(bullets[bulletIndex]);
  }

  return step.note ? estimateStreamMs(step.note) : 0;
}
